/**
 * Text cleaning and formatting utilities
 */

/**
 * Cleans and normalizes text content
 */
export function cleanText(text: string): string {
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\t/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Truncates text to specified length with ellipsis
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

/**
 * Converts markdown-style bold text to HTML
 */
export function markdownToHtml(text: string): string {
  return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
}

/**
 * Converts markdown-style bold text to plain text
 */
export function stripMarkdown(text: string): string {
  return text.replace(/\*\*(.*?)\*\*/g, '$1');
}

/**
 * Formats paragraphs with proper spacing
 */
export function formatParagraphs(text: string): string[] {
  return text
    .split('\n')
    .map(p => p.trim())
    .filter(p => p.length > 0);
}

/**
 * Strips HTML tags from text content
 */
export function stripHtml(html: string): string {
  if (!html) return '';
  
  // Create a temporary DOM element
  const temp = document.createElement('div');
  temp.innerHTML = html;
  
  // Get text content and clean it
  return cleanText(temp.textContent || temp.innerText || '');
}

/**
 * Counts words in a text string
 */
export function countWords(text: string): number {
  return text
    .trim()
    .split(/\s+/)
    .filter(word => word.length > 0)
    .length;
}

/**
 * Estimates reading time in minutes
 */
export function estimateReadingTime(text: string): number {
  const wordsPerMinute = 200;
  const words = countWords(text);
  return Math.ceil(words / wordsPerMinute);
}